import Store from '@store';
import PageThumbnailFactory from 'assets/js/editor/factory/page-thumbnail';
import { sanitizeSavingContent } from 'src/utils/sanitize-content';

import waitForPageLoad from '../../../../helpers/waitForPageLoad';
import AppDecorate from '../../../app/decorate';
import { instance as CompanyFactory } from '../../../factory/company';
import EditorModeManager from '../../../ui/editor-mode-manager';
import pdfProcessingManager from '../../../utils/pdf-processing-manager.js';

import displayUndraftModal from './undraft-modal';

const selectionDisableClass = 'screenshot-temp-canvas';
let templateData;

export const errorMessages = {
  userNotFound: 'Username could not be found',
  saveFailed: 'Saving page failed',
  offline: 'Save failed. Check your connection and try again.'
};

module.exports = AppDecorate(require('../app/page'), _.extend({
  initialize: function () {
    this.savingDeferred = null;
    this.tool = INKIVE.Tool.getInstance();
    this.editor = this.tool.editor;
    this.canvas = this.editor.canvas;
  },

  unload: function (params) {
    const editorFacade = require('../editor');

    params = params || {};
    params.page_number = params.page_number || INKIVE.Tool._current_page_number;
    params.remove_from_cache && editorFacade.handle(
      'cache', 'remove', { page_number: params.page_number }
    );

    if (INKIVE.Tool._current_page_number === params.page_number) {
      INKIVE.Tool._current_design_id = null;
      INKIVE.Tool._current_page_number = 0;
    }

    this.trigger('unloaded');
  },

  moveTo: async function ({
    save,
    quiet,
    page_number: pageNumber,
    design_id: designId,
    page_is_empty: pageIsEmpty
  }) {
    INKIVE.Tool._previous_page_number = INKIVE.Tool._current_page_number;
    const hasToSave = INKIVE.Tool._current_page_number >= 1 &&
      save !== false &&
      !document.getElementById('svgroot').classList.contains(selectionDisableClass)
    ;

    const shouldTogglePageLoader = hasToSave && INKIVE.hasEditorUnsavedChanges && !INKIVE.C.SHOW_TEXT_OVERFLOW;

    const deferred = $.Deferred();
    shouldTogglePageLoader && this.canvas.loadingElement(true);

    if (!pageNumber && designId) {
      const design = INKIVE.ProjectFacade.get('design', { design_id: designId });
      if (design) pageNumber = design.page_number;
    }

    const isAlreadyThere = designId
      ? designId === INKIVE.Tool._current_design_id
      : pageNumber === INKIVE.Tool._current_page_number;

    if (isAlreadyThere) {
      deferred.resolve();

      return deferred.promise();
    }

    if (!quiet && shouldTogglePageLoader) {
      INKIVE.Modal.togglePageLoader(true);
    }

    if (hasToSave) {
      const hasTextMock = document.querySelector('#textmock')?.childNodes?.length;
      const hasUpdates = await INKIVE.PageFactory.hasUpdates();
      const shouldSaveState = hasTextMock || hasUpdates || Store.getState()?.metadata.bullet_and_ordered_list;

      if (shouldSaveState) {
        await this.saveState();
      } else {
        await this.save({ quiet: true, info: 'save on move_to' });
      }
    }

    try {
      await this._moveTo(pageNumber, pageIsEmpty);
      this.canvas.loadingElement(false);
    } catch (err) {
      this.canvas.loadingElement(false);
      console.error(err);
    }

    INKIVE.Modal.closeAllPageLoaders();
    quiet || INKIVE.Modal.togglePageLoader(false);
    deferred.resolve();

    return deferred.promise();
  },

  saveState: async function () {
    const svgContent = sanitizeSavingContent(INKIVE.EditorFacade.editor.getSvgString());
    const state = Store.getState();
    const promises = [];
    const hasHeaderAndFooterChanges = INKIVE.EditorFacade.handle('header_and_footer', 'has_changes');
    const hasTextOverflowNodes = INKIVE.TextOverflowFactory.hasLinkedNodes();

    INKIVE.TextOverflowFactory.renderHiddenTextsOverflow();
    const additionalTexts = INKIVE.AdvancedOrderedListFactory.renderHiddenTexts();

    INKIVE.TextOverflowFactory.setChange(false);

    // Save state of current page
    promises.push(INKIVE.PageFactory.savePageState(INKIVE.Tool._current_design_id, svgContent));
    INKIVE.EditorFacade.trigger('page.saved.content', { id: INKIVE.Tool._current_design_id, content: svgContent, skipRenderThumbnail: true });

    // Save state of other pages that have changes
    const updatedPagesContent = INKIVE.TextOverflowFactory.getUpdatedPagesContentWithHiddenText(state.page, additionalTexts);
    for (const [id, content] of Object.entries(updatedPagesContent)) {
      if (Number(id) === INKIVE.Tool._current_design_id) continue;

      promises.push(INKIVE.PageFactory.savePageState(id, content));
      INKIVE.EditorFacade.trigger('page.saved.content', { id, content, skipRenderThumbnail: true });
    }

    await Promise.all(promises);

    if (hasHeaderAndFooterChanges) {
      await INKIVE.EditorFacade.handle('header_and_footer', 'save');
    }
    if (hasHeaderAndFooterChanges || hasTextOverflowNodes) {
      await INKIVE.Helpers.delay(500); // Remove when fixed the issue HF/TO saving is not async
    }
  },

  save: async function (params, opts) {
    if (this.savingDeferred && params && !params.additional_props) {
      return this.savingDeferred.promise();
    }

    this.savingDeferred = $.Deferred();

    if (this._isLegacySave()) {
      this._handleLegacySave();
    } else {
      try {
        const changes = await this._getChangesToSave(params, opts);
        if (!changes && !INKIVE.hasEditorUnsavedChanges) throw new Error('no changes');

        await waitForPageLoad();
        // _handleDefaultPageSave triggers promise resolve, It doesn't need to be awaited
        this._handleDefaultPageSave(changes, params);
      } catch (error) {
        this._handleNothingToSave(params);
      }
    }

    return this.savingDeferred.promise();
  },

  loadVersion: function (params) {
    if (!params?.version_id) {
      return;
    }

    const designId = params.design_id || INKIVE.Tool._current_design_id;
    const updatedParams = { ...params, design_id: designId };

    const design = INKIVE.ProjectFacade.get('design', { design_id: designId });
    INKIVE.EditorFacade.handle('cache', 'remove', { design_id: designId });

    this.tool.load(
      design.page_number,
      _.bind(this.trigger, this, 'load_version', updatedParams),
      params.version_id
    );

    this.canvas.runExtensions('hideHeaderAndFooter', {});
  },

  hideSelectionHandlers: function (hide) {
    $('#selectorParentGroup').toggle(!hide);
  },

  _getChangesToSave: function (params, opts) {
    const deferred = $.Deferred();
    const currentLayer = this.canvas
      .getCurrentDrawing()
      .getCurrentLayer();

    const isClippingImage = currentLayer && currentLayer.getAttribute('class') === 'clipperImage';
    let hasDraftChanges = opts && opts.undraft;
    // hasDesignChanges is always true atm,
    // since the areEqual check is returning false in scenarios where it shouldn't
    const hasDesignChanges = params?.force_save || INKIVE.hasEditorUnsavedChanges;
    const hasRestrictionChanges = INKIVE.RestrictionsFactory && !INKIVE.RestrictionsFactory.isAllSaved();
    const hasPlaceholderChanges = INKIVE.PlaceholdersFactory && INKIVE.PlaceholdersFactory.haveChanged();
    const hasAnimationChanges = INKIVE.AnimationFactory.hasChanges();
    const hasHeaderAndFooterChanges = INKIVE.EditorFacade.handle('header_and_footer', 'has_changes');
    const hasTextOverflowChanges = INKIVE.TextOverflowFactory.hasChanges();
    const hasChangesToSave = !isClippingImage && (
      hasDesignChanges ||
      hasRestrictionChanges ||
      hasDraftChanges ||
      hasHeaderAndFooterChanges ||
      hasTextOverflowChanges ||
      hasPlaceholderChanges ||
      hasAnimationChanges
    );

    if (isClippingImage || INKIVE.C.BOOK.book_type !== INKIVE.C.BOOK_TYPE.TEMPLATE) { resolve(); } else if (!params || !params.confirm_draft || hasDraftChanges === false) { resolve(); } else { checkDraftTemplate(); }

    return deferred.promise();

    function checkDraftTemplate () {
      templateData && onTemplateData();
      templateData || CompanyFactory.get(['templates', INKIVE.C.BOOK.encodedId]).then((td) => {
        templateData = td;
        return onTemplateData();
      });

      function onTemplateData () {
        (params.askedConfirmation || !templateData.is_draft || templateData.is_under_review)
          ? resolve()
          : askConfirmation();
      }

      function askConfirmation () {
        if (INKIVE.Modal.lockStatus || !hasChangesToSave || INKIVE.C.REVIEW_MODE) return resolve();

        displayUndraftModal(() => {
          params.askedConfirmation = true;
          hasDraftChanges = true;
          resolve();
        }, () => {
          hasDraftChanges = false;
          resolve();
        });
      }
    }

    function resolve () {
      deferred.resolve(hasChangesToSave
        ? {
            animation: hasAnimationChanges,
            headerAndFooter: hasHeaderAndFooterChanges,
            textOverflow: hasTextOverflowChanges,
            undraft: hasDraftChanges,
            design: hasDesignChanges,
            restrictions: hasRestrictionChanges,
            placeholders: hasPlaceholderChanges
          }
        : null);
    }
  },

  _isLegacySave: function () {
    return _.contains([
      'template',
      'sticker',
      'banner',
      'placeholder'
    ], EditorModeManager.getMode());
  },

  _handleLegacySave: function () {
    this.tool.save(null, () => {
      const deferred = this.savingDeferred;
      this.savingDeferred = null;
      deferred.resolve();
    });
  },

  _handleNothingToSave: function (params) {
    params = params || { quiet: false, no_events: false };

    _.defer(() => { this.savingDeferred = null; });
    this.savingDeferred.resolve(params.quiet);
    params.no_events || this.trigger('already_saved', params);
    params.quiet || this.editor.canvas.call('advice.show', 'already-saved');
  },

  _handleDefaultPageSave: function (changes, data) {
    const promises = [];
    const hasChanges = (changes && Object.values(changes).some(Boolean)) || INKIVE.hasEditorUnsavedChanges;
    const params = data || { quiet: false, no_events: false };

    params.no_events || this.trigger('saving', data);
    if (!params.quiet && INKIVE.DesignFacade.canvas.getMode() === 'textedit') {
      INKIVE.DesignFacade.canvas.setMode('select');
    }
    if (changes?.restrictions) {
      promises.push(INKIVE.RestrictionsFactory.save());
    }

    if (changes?.headerAndFooter) {
      promises.push((() => {
        const deferred = $.Deferred();
        INKIVE.EditorFacade.handle('header_and_footer', 'save')
          .then(() => deferred.resolve())
          .catch(() => deferred.resolve());
        return deferred.promise();
      })());
    }

    if (changes?.placeholders) {
      promises.push(INKIVE.PlaceholdersFactory?.save({ undraft: changes.undraft }));
    }

    return $.when.apply($, promises).then(
      _.debounce(_allDone.bind(this, true), 0),
      _.debounce(_allDone.bind(this, false), 0)
    );

    function _allDone (success) {
      const deferred = this.savingDeferred;
      this.savingDeferred = null;

      if (deferred) {
        if (success) {
          if (hasChanges || params.additional_props) {
            this.tool.savePage(params.quiet, result => this._onSaved(deferred, result, changes, params), false, params.additional_props);
          } else {
            INKIVE.PageFactory.propagateStateChanges();
            INKIVE.hasEditorUnsavedChanges = false;
            deferred.resolve();
          }
        } else {
          deferred.reject(new Error('Done Saving page failed'));
        }
      }
    }
  },

  _onSaved: async function (deferred, result, changes, params) {
    const isOffline = navigator.onLine === false;

    try {
      if (result) {
        await INKIVE.PageFactory.propagateStateChanges();

        params.quiet || this.editor.canvas.call('advice.show', 'saved');
        changes?.undraft && await CompanyFactory._undraft();
        this.trigger('saved');
        INKIVE.hasEditorUnsavedChanges = false;
        deferred.resolve();
      } else {
        const message = isOffline ? errorMessages.offline : errorMessages.saveFailed;

        deferred.reject(new Error(message));
      }
      this.savingDeferred = null;
    } catch (error) {
      const message = isOffline ? errorMessages.offline : errorMessages.userNotFound;

      error.message += message;

      deferred.reject(new Error(error));
      this.trigger('saving.error', { error: message });
    }
  },

  _moveTo: async function (pageNumber, pageIsEmpty) {
    const isBeingProcessed = !pageIsEmpty &&
      await pdfProcessingManager.isBeingProcessed(pageNumber);

    this._toggleUI(!isBeingProcessed);

    if (isBeingProcessed) {
      const design = INKIVE.ProjectFacade.get('design', { page_number: pageNumber });

      if (design) {
        await this._loadFakePage(design);
        INKIVE.Tool._current_design_id = design.design_id;
      } else {
        INKIVE.Tool._current_design_id = null;

        $.ajax({
          type: 'post',
          dataType: 'json',
          url: `${BASE_URL}untidy-api/log/critical`,
          data: {
            name: 'Missing INKIVE.Tool._current_design_id',
            data: {
              file: 'page.js',
              ref: 'isBeingProcessed',
              book: INKIVE.C.BOOK.id
            }
          }
        });
      }

      INKIVE.Tool._current_page_number = pageNumber;
    } else {
      await new Promise(resolve => this.tool.load(pageNumber, resolve, null, pageIsEmpty));
    }

    this.trigger('move_to', { page_number: pageNumber });
    INKIVE.C.IS_MOBILE && this.tool.updateCurrentPageInfo();
  },

  _loadFakePage: async function (design) {
    const thumb = await new Promise(resolve => {
      const thumb = PageThumbnailFactory.get(design.design_id, design);

      const imgLoader = new Image();
      imgLoader.src = thumb;

      imgLoader.onload = () => resolve(imgLoader);
      imgLoader.onerror = () => resolve(null);
    });

    let svgContent = Base64.decode(INKIVE.C.BLANK_SVG);

    if (thumb) {
      svgContent = svgContent
        .replace(/width="\d+"/g, `width="${thumb.width}"`)
        .replace(/height="\d+"/g, `height="${thumb.height}"`);
    }

    this.editor.loadSvgString(svgContent);

    if (thumb) {
      const img = this.canvas.addElement('image', {
        x: 0,
        y: 0,
        url: thumb.src,
        width: thumb.width,
        height: thumb.height
      });

      img.classList.add(selectionDisableClass);
      img.style.cursor = 'not-allowed';
    }
  },

  _toggleUI: function (enable = true) {
    const popupMenu = document.querySelector('#element-popupmenu > .root-popupmenu');
    const svgroot = document.getElementById('svgroot');

    if (enable) {
      INKIVE.Tool.enableUI();
      popupMenu?.classList?.remove('disabled');
      svgroot.classList.remove(selectionDisableClass);
      svgroot.style.cursor = '';
    } else {
      INKIVE.Tool.disableUI();
      document.getElementById('pages-panel').classList.remove('disabled');
      popupMenu?.classList?.add('disabled');
      svgroot.classList.add(selectionDisableClass);
      svgroot.style.cursor = 'not-allowed';
      this.canvas.call('advice.show', 'processing-pdf');
    }
  }
}, Backbone.Events));
